import React, { useEffect, useState, FormEvent } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  FunnelIcon,
  UserGroupIcon,
  ChartBarIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { getAuthHeaders } from '../utils/auth';
import { API_URL } from '../constants/urls';

type AreaKPI = {
  name: string;
  description?: string;
  target?: any;
  current?: any;
  unit?: string;
  frequency?: string;
};

type AreaMember = {
  id: string;
  user_id: string;
  user_name?: string;
  user_email?: string;
  role: string;
  joined_at: string;
};

type Area = {
  id: string;
  name: string;
  description?: string;
  value: string;
  color?: string;
  icon?: string;
  is_active: boolean;
  is_default: boolean;
  display_order: number;
  owner_id?: string;
  owner_name?: string;
  maturity_level?: 'emerging' | 'developing' | 'mature' | 'optimized';
  kpis?: AreaKPI[];
  created_at: string;
  updated_at: string;
  members?: AreaMember[];
  member_count?: number;
  task_count?: number;
  okr_count?: number;
  research_count?: number;
};

type AreaFormData = {
  name: string;
  description: string;
  color: string;
  icon: string;
  is_active: boolean;
  display_order: number;
  owner_id: string;
  maturity_level: string;
  kpis: AreaKPI[];
};

const INITIAL_FORM_DATA: AreaFormData = {
  name: '',
  description: '',
  color: '#3B82F6',
  icon: 'folder',
  is_active: true,
  display_order: 999,
  owner_id: '',
  maturity_level: 'developing',
  kpis: [],
};

const MATURITY_LEVELS = [
  {
    value: 'emerging',
    label: 'Emerging',
    color: 'bg-yellow-100 text-yellow-800',
  },
  {
    value: 'developing',
    label: 'Developing',
    color: 'bg-blue-100 text-blue-800',
  },
  { value: 'mature', label: 'Mature', color: 'bg-green-100 text-green-800' },
  {
    value: 'optimized',
    label: 'Optimized',
    color: 'bg-purple-100 text-purple-800',
  },
];

const ICON_OPTIONS = [
  'folder',
  'cube',
  'code',
  'megaphone',
  'chart',
  'users',
  'cog',
  'currency',
  'heart',
  'shield',
  'rocket',
  'lightbulb',
];

export default function AreasTailwind() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const {
    selectedOrg: organization,
    userRole: userOrgRole,
    canEdit: userCanEdit,
  } = useOrganization();
  const [areas, setAreas] = useState<Area[]>([]);
  const [orgMembers, setOrgMembers] = useState<
    Array<{ user_id: string; user_name: string }>
  >([]);
  const [open, setOpen] = useState(false);
  const [editingArea, setEditingArea] = useState<Area | null>(null);
  const [formData, setFormData] = useState<AreaFormData>(INITIAL_FORM_DATA);
  const [searchTerm, setSearchTerm] = useState('');
  const [showKPIForm, setShowKPIForm] = useState(false);
  const [newKPI, setNewKPI] = useState<AreaKPI>({
    name: '',
    description: '',
    target: '',
    current: '',
    unit: '',
    frequency: '',
  });

  // Sorting state
  const [sortField, setSortField] = useState<keyof Area>('display_order');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Filtering state
  const [filters, setFilters] = useState({
    maturity_level: '',
    is_active: '',
    has_owner: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  const canDelete = userOrgRole === 'admin' || userOrgRole === 'owner';

  const getAuthConfig = () => ({
    headers: getAuthHeaders(),
  });

  useEffect(() => {
    if (!organization) return;
    fetchAreas();
    fetchOrgMembers();
  }, [organization]);

  const fetchAreas = async () => {
    if (!organization || !token) return;
    try {
      const response = await axios.get(
        `${API_URL}/areas?organization_id=${organization.id}`,
        getAuthConfig(),
      );
      console.log('Areas data received:', response.data);
      // Log KPI details for debugging
      response.data.forEach((area: any) => {
        console.log(
          `Area: ${area.name}, KPIs:`,
          area.kpis,
          `Count: ${area.kpis?.length || 0}`,
        );
      });
      setAreas(response.data);
    } catch (error) {
      console.error('Error fetching areas:', error);
    }
  };

  const fetchOrgMembers = async () => {
    if (!organization || !token) return;
    try {
      const response = await axios.get(
        `${API_URL}/organizations/${organization.id}/members`,
        getAuthConfig(),
      );
      setOrgMembers(response.data);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setEditingArea(null);
    setFormData(INITIAL_FORM_DATA);
    setShowKPIForm(false);
    setNewKPI({
      name: '',
      description: '',
      target: '',
      current: '',
      unit: '',
      frequency: '',
    });
  };

  const handleEdit = (area: Area) => {
    setEditingArea(area);
    setFormData({
      name: area.name || '',
      description: area.description || '',
      color: area.color || '#3B82F6',
      icon: area.icon || 'folder',
      is_active: area.is_active ?? true,
      display_order: area.display_order || 999,
      owner_id: area.owner_id || '',
      maturity_level: area.maturity_level || 'developing',
      kpis: area.kpis || [],
    });
    setOpen(true);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!organization || !token) return;

    const submitData = {
      ...formData,
      owner_id: formData.owner_id === '' ? null : formData.owner_id,
    };

    console.log('Submitting area with KPIs:', submitData.kpis);

    try {
      if (editingArea) {
        const response = await axios.put(
          `${API_URL}/areas/${editingArea.id}?organization_id=${organization.id}`,
          submitData,
          getAuthConfig(),
        );
        console.log('Update response with KPIs:', response.data.kpis);
        setAreas(
          areas.map((a) => (a.id === editingArea.id ? response.data : a)),
        );
      } else {
        // Create new area - value will be generated from name on backend
        const response = await axios.post(
          `${API_URL}/areas?organization_id=${organization.id}`,
          {
            ...submitData,
            value: formData.name.toLowerCase().replace(/\s+/g, '_'),
          },
          getAuthConfig(),
        );
        console.log('Create response with KPIs:', response.data.kpis);
        setAreas([...areas, response.data]);
      }
      handleClose();
    } catch (error) {
      console.error('Error saving area:', error);
      alert('Failed to save area. Please try again.');
    }
  };

  const handleDelete = async (areaId: string) => {
    if (!organization || !token) return;

    // Check if user has permission (admin/owner or department owner)
    const area = areas.find((a) => a.id === areaId);
    const currentUserId = localStorage.getItem('user_id');
    const canDeleteThis = canDelete || area?.owner_id === currentUserId;

    if (!canDeleteThis) {
      alert("You don't have permission to delete this department");
      return;
    }

    if (!window.confirm('Are you sure you want to delete this department?'))
      return;

    try {
      const response = await axios.delete(
        `${API_URL}/areas/${areaId}?organization_id=${organization.id}`,
        getAuthConfig(),
      );
      setAreas(areas.filter((a) => a.id !== areaId));
      // Show success message if tasks were reassigned
      if (response.data?.message) {
        alert(response.data.message);
      }
    } catch (error: any) {
      console.error('Error deleting department:', error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to delete department. It may be in use.';
      alert(errorMessage);
    }
  };

  const handleAddKPI = () => {
    if (newKPI.name) {
      setFormData({
        ...formData,
        kpis: [...formData.kpis, newKPI],
      });
      setNewKPI({
        name: '',
        description: '',
        target: '',
        current: '',
        unit: '',
        frequency: '',
      });
      setShowKPIForm(false);
    }
  };

  const handleRemoveKPI = (index: number) => {
    setFormData({
      ...formData,
      kpis: formData.kpis.filter((_, i) => i !== index),
    });
  };

  const handleCreateDefaults = async () => {
    if (!organization || !token) return;

    const defaultDepartments = [
      {
        name: 'Product & Engineering',
        description: 'Product development and technical innovation',
      },
      {
        name: 'Marketing & Brand',
        description: 'Brand management, marketing campaigns, and growth',
      },
      {
        name: 'Sales & Business Development',
        description: 'Revenue generation and partnership development',
      },
      {
        name: 'Customer Success',
        description: 'Customer support, retention, and satisfaction',
      },
      {
        name: 'Finance & Operations',
        description: 'Financial management and operational excellence',
      },
      {
        name: 'People & Culture',
        description: 'HR, talent management, and organizational culture',
      },
      {
        name: 'Legal & Compliance',
        description: 'Legal affairs, contracts, and regulatory compliance',
      },
      {
        name: 'Service Design & Innovation',
        description: 'Service innovation and design thinking',
      },
      {
        name: 'Data & Analytics',
        description: 'Data strategy, analytics, and business intelligence',
      },
    ];

    try {
      for (const dept of defaultDepartments) {
        await axios.post(
          `${API_URL}/areas?organization_id=${organization.id}`,
          {
            name: dept.name,
            description: dept.description,
            is_active: true,
            maturity_level: 'developing',
          },
          getAuthConfig(),
        );
      }
      // Refresh the departments list
      fetchAreas();
    } catch (error) {
      console.error('Error creating default departments:', error);
      alert(
        'Failed to create default departments. Some may have been created.',
      );
    }
  };

  // Sorting function
  const handleSort = (field: keyof Area) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort areas
  const filteredAndSortedAreas = areas
    .filter((area) => {
      if (
        searchTerm &&
        !area.name.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false;
      }
      if (
        filters.maturity_level &&
        area.maturity_level !== filters.maturity_level
      ) {
        return false;
      }
      if (
        filters.is_active &&
        area.is_active.toString() !== filters.is_active
      ) {
        return false;
      }
      if (filters.has_owner === 'true' && !area.owner_id) {
        return false;
      }
      if (filters.has_owner === 'false' && area.owner_id) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (!sortField) return 0;
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const getMaturityBadge = (level?: string) => {
    const maturity = MATURITY_LEVELS.find((m) => m.value === level);
    if (!maturity) return null;
    return (
      <span
        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${maturity.color}`}
      >
        {maturity.label}
      </span>
    );
  };

  return (
    <div className="flex-1 flex flex-col bg-gray-100 min-h-screen">
      <div className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Departments</h1>
              <p className="text-gray-600 mt-1">
                Manage organizational departments and their KPIs
              </p>
            </div>
            {userCanEdit && (
              <button
                onClick={handleOpen}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                New Department
              </button>
            )}
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-white shadow rounded-lg mb-6 p-4">
            <div className="flex gap-4 items-center">
              <input
                type="text"
                placeholder="Search departments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <FunnelIcon className="h-5 w-5 mr-2" />
                Filters
              </button>
            </div>

            {/* Filter Options */}
            {showFilters && (
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Maturity Level
                  </label>
                  <select
                    value={filters.maturity_level}
                    onChange={(e) =>
                      setFilters({ ...filters, maturity_level: e.target.value })
                    }
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="">All</option>
                    {MATURITY_LEVELS.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    value={filters.is_active}
                    onChange={(e) =>
                      setFilters({ ...filters, is_active: e.target.value })
                    }
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="">All</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Has Owner
                  </label>
                  <select
                    value={filters.has_owner}
                    onChange={(e) =>
                      setFilters({ ...filters, has_owner: e.target.value })
                    }
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="">All</option>
                    <option value="true">Has Owner</option>
                    <option value="false">No Owner</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Areas Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Department Name
                      {sortField === 'name' &&
                        (sortDirection === 'asc' ? (
                          <ChevronUpIcon className="h-4 w-4 ml-1" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4 ml-1" />
                        ))}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Owner
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('maturity_level')}
                  >
                    <div className="flex items-center">
                      Maturity
                      {sortField === 'maturity_level' &&
                        (sortDirection === 'asc' ? (
                          <ChevronUpIcon className="h-4 w-4 ml-1" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4 ml-1" />
                        ))}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    KPIs
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Stats
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedAreas.map((area) => (
                  <tr key={area.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {area.color && (
                          <div
                            className="w-8 h-8 rounded-full mr-3 flex items-center justify-center text-white font-semibold"
                            style={{ backgroundColor: area.color }}
                          >
                            {area.name[0]}
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium">
                            <Link
                              to={`/areas/${area.id}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {area.name}
                            </Link>
                          </div>
                          {area.description && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {area.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {area.owner_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getMaturityBadge(area.maturity_level)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <ChartBarIcon className="h-4 w-4 mr-1" />
                        {area.kpis?.length || 0} KPIs
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="space-y-1">
                        <div className="flex items-center">
                          <UserGroupIcon className="h-4 w-4 mr-1" />
                          {area.member_count || 0} members
                        </div>
                        <div className="flex items-center">
                          <SparklesIcon className="h-4 w-4 mr-1" />
                          {area.task_count || 0} tasks
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          area.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {area.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => navigate(`/areas/${area.id}`)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="View details"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      {userCanEdit && (
                        <button
                          onClick={() => handleEdit(area)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                      )}
                      {(canDelete ||
                        area.owner_id === localStorage.getItem('user_id')) && (
                        <button
                          onClick={() => handleDelete(area.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete department"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredAndSortedAreas.length === 0 && (
              <div className="text-center py-12">
                {areas.length === 0 ? (
                  // True empty state - no departments exist
                  <>
                    <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No departments yet
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Get started by creating your first department or use our
                      recommended defaults.
                    </p>
                    {userCanEdit && (
                      <div className="mt-6 flex justify-center gap-3">
                        <button
                          onClick={handleCreateDefaults}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <SparklesIcon className="h-5 w-5 mr-2 text-gray-400" />
                          Create Default Departments
                        </button>
                        <button
                          onClick={handleOpen}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <PlusIcon className="h-5 w-5 mr-2" />
                          Create Custom Department
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  // Filtered empty state - departments exist but none match filters
                  <>
                    <p className="text-gray-500">
                      No departments match your filters.
                    </p>
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setFilters({
                          maturity_level: '',
                          is_active: '',
                          has_owner: '',
                        });
                      }}
                      className="mt-4 text-sm text-blue-600 hover:text-blue-500"
                    >
                      Clear filters
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {open && (
        <div className="fixed inset-0 bg-gray-500/75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-semibold mb-4">
              {editingArea ? 'Edit Department' : 'Create New Department'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Area Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    required
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Owner
                  </label>
                  <select
                    value={formData.owner_id}
                    onChange={(e) =>
                      setFormData({ ...formData, owner_id: e.target.value })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">No owner</option>
                    {orgMembers.map((member) => (
                      <option key={member.user_id} value={member.user_id}>
                        {member.user_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Maturity Level
                  </label>
                  <select
                    value={formData.maturity_level}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        maturity_level: e.target.value,
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    {MATURITY_LEVELS.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Color
                  </label>
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) =>
                      setFormData({ ...formData, color: e.target.value })
                    }
                    className="mt-1 block h-10 w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Icon
                  </label>
                  <select
                    value={formData.icon}
                    onChange={(e) =>
                      setFormData({ ...formData, icon: e.target.value })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    {ICON_OPTIONS.map((icon) => (
                      <option key={icon} value={icon}>
                        {icon}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        display_order: parseInt(e.target.value) || 0,
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    value={formData.is_active.toString()}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        is_active: e.target.value === 'true',
                      })
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                {/* KPIs Section */}
                <div className="sm:col-span-2">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Key Performance Indicators
                      {formData.kpis.length > 0 && (
                        <span className="ml-2 text-xs text-amber-600 font-normal">
                          ({formData.kpis.length} unsaved)
                        </span>
                      )}
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowKPIForm(true)}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <PlusIcon className="h-3 w-3 mr-1" />
                      Add KPI
                    </button>
                  </div>

                  {/* KPI Form */}
                  {showKPIForm && (
                    <div className="border rounded-md p-3 mb-2 bg-gray-50">
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          placeholder="KPI Name *"
                          value={newKPI.name}
                          onChange={(e) =>
                            setNewKPI({ ...newKPI, name: e.target.value })
                          }
                          className="block w-full border-gray-300 rounded-md shadow-sm text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Unit (e.g., %, count)"
                          value={newKPI.unit}
                          onChange={(e) =>
                            setNewKPI({ ...newKPI, unit: e.target.value })
                          }
                          className="block w-full border-gray-300 rounded-md shadow-sm text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Target Value"
                          value={newKPI.target}
                          onChange={(e) =>
                            setNewKPI({ ...newKPI, target: e.target.value })
                          }
                          className="block w-full border-gray-300 rounded-md shadow-sm text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Current Value"
                          value={newKPI.current}
                          onChange={(e) =>
                            setNewKPI({ ...newKPI, current: e.target.value })
                          }
                          className="block w-full border-gray-300 rounded-md shadow-sm text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Description"
                          value={newKPI.description}
                          onChange={(e) =>
                            setNewKPI({
                              ...newKPI,
                              description: e.target.value,
                            })
                          }
                          className="block w-full border-gray-300 rounded-md shadow-sm text-sm"
                        />
                        <select
                          value={newKPI.frequency}
                          onChange={(e) =>
                            setNewKPI({ ...newKPI, frequency: e.target.value })
                          }
                          className="block w-full border-gray-300 rounded-md shadow-sm text-sm"
                        >
                          <option value="">Frequency</option>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                      </div>
                      <div className="mt-2 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setShowKPIForm(false)}
                          className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleAddKPI}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}

                  {/* KPI List */}
                  {formData.kpis.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-amber-600 italic">
                        Remember to click{' '}
                        {editingArea
                          ? '"Update Department"'
                          : '"Create Department"'}{' '}
                        below to save these KPIs
                      </p>
                      {formData.kpis.map((kpi, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center p-2 bg-gray-50 rounded-md"
                        >
                          <div className="text-sm">
                            <span className="font-medium">{kpi.name}</span>
                            {kpi.target && (
                              <span className="text-gray-500 ml-2">
                                Target: {kpi.target} {kpi.unit}
                              </span>
                            )}
                            {kpi.current && (
                              <span className="text-gray-500 ml-2">
                                Current: {kpi.current} {kpi.unit}
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveKPI(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                    formData.kpis.length > 0
                      ? 'bg-amber-600 hover:bg-amber-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {editingArea
                    ? formData.kpis.length > 0
                      ? 'Update Department & Save KPIs'
                      : 'Update Department'
                    : formData.kpis.length > 0
                      ? 'Create Department & Save KPIs'
                      : 'Create Department'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
